import { EChart } from "@kbox-labs/react-echarts";
import { CallbackDataParams } from "echarts/types/dist/shared";
import { AdvancedSearchRequest, AdvancedSearchResultSummary } from "generated/graphql";
import React, { useMemo } from "react";

interface AdvancedSearchSummaryProps {
    request: AdvancedSearchRequest;
    summaries: AdvancedSearchResultSummary[];
}

interface AdvancedSearchChartItemProps {
    summary: AdvancedSearchResultSummary;
}

const AdvancedSearchChartItem: React.FC<AdvancedSearchChartItemProps> = props => {
    const { summary } = props;

    const chartData = useMemo(() => {
        return summary.groups.map(group => [
            group.groupKey?.[0]?.formattedValue || "(blank)",
            group.aggregates[0]?.numberValue ?? 0,
            group.aggregates[0]?.formattedValue ?? "",
        ]);
    }, [summary]);

    // Tailwind color palette (700 and 300 variants)
    const barColors = [
        "#0e7490", // cyan-700
        "#c2410c", // orange-700
        "#15803d", // green-700
        "#a16207", // yellow-700
        "#4338ca", // indigo-700
        "#1d4ed8", // blue-700
        "#be185d", // pink-700
        "#7e22ce", // purple-700
        "#be123c", // rose-700
        "#0f766e", // teal-700
        "#b91c1c", // red-700
    ];

    return (
        <div className="w-full" style={{ height: "350px" }}>
            <EChart
                renderer="svg"
                className="[&>div]:!w-full"
                style={{
                    height: "350px",
                    width: "100%",
                }}
                grid={{
                    top: "15px", // Adjust the top margin of the chart to reduce space for the title
                    left: "30px",
                    right: "20px",
                    bottom: "80px",
                }}
                xAxis={{
                    type: "category",
                    axisLabel: {
                        interval: 0,
                        rotate: 30,
                    },
                    axisLine: {
                        show: false,
                    },
                }}
                yAxis={{
                    type: "value",
                    boundaryGap: [0, "20%"],
                    splitLine: {
                        show: true,
                    },
                    axisLabel: {
                        show: true,
                    },
                }}
                series={[
                    {
                        type: "bar",
                        data: chartData,
                        itemStyle: {
                            color: (params: { dataIndex: number }) =>
                                barColors[params.dataIndex % barColors.length],
                        },
                        label: {
                            show: true,
                            position: "top",
                            formatter: (params: CallbackDataParams): string => {
                                if (Array.isArray(params.value) && params.value[2]) {
                                    return String(params.value[2]);
                                }
                                const value = Array.isArray(params.value)
                                    ? params.value[1]
                                    : params.value;
                                if (typeof value === "number") return value.toString();
                                if (value instanceof Date) return value.toISOString();
                                return String(value);
                            },
                        },
                    },
                ]}
            />
        </div>
    );
};

export const AdvancedSearchChart: React.FC<AdvancedSearchSummaryProps> = props => {
    const { request, summaries } = props;

    if (
        !summaries ||
        summaries.length === 0 ||
        !request.summaries ||
        request.summaries.length === 0
    ) {
        return null;
    }

    return (
        <ul className="space-y-4">
            {summaries.map((summary, index) => (
                <li key={index}>
                    <AdvancedSearchChartItem summary={summary} />
                </li>
            ))}
        </ul>
    );
};
